import {useState, useEffect, useCallback, useMemo} from 'react';
import {useChat} from '@ai-sdk/react';
import {isToolUIPart, getToolName} from 'ai';
import type {UIMessage, ToolUIPart} from 'ai';
import {CassandraChatTransport, createChat, getMessages, setCognitoIdTokenProvider, setUserId} from './cassandra-api';
import type {Credentials} from 'aws-sdk';
import {fetchAuthSession} from 'aws-amplify/auth';

interface UseCassandraChatOptions {
    credentials: Credentials | undefined;
    email: string | undefined;
    activeChatId: string | null;
    setActiveChatId: (id: string | null) => void;
}

export function useCassandraChat({credentials, email, activeChatId, setActiveChatId}: UseCassandraChatOptions) {
    const [pendingText, setPendingText] = useState<string | null>(null);
    const [input, setInput] = useState('');

    // Auth setup
    useEffect(() => {
        if (email) {
            setUserId(email);
            setCognitoIdTokenProvider(async () => {
                const session = await fetchAuthSession();
                return session.tokens?.idToken?.toString() ?? '';
            });
        }
    }, [email]);

    const transport = useMemo(() => (credentials ? new CassandraChatTransport(credentials) : null), [credentials]);

    const {messages, sendMessage, status, setMessages, stop, addToolResult} = useChat({
        id: activeChatId ?? undefined,
        transport: transport ?? undefined,
        sendAutomaticallyWhen: ({messages: msgs}: {messages: UIMessage[]}) => {
            const last = msgs.at(-1);
            if (!last || last.role !== 'assistant') return false;
            const questionParts = last.parts.filter((p) => isToolUIPart(p) && getToolName(p as ToolUIPart) === 'askQuestion');
            return questionParts.length > 0 && questionParts.every((p) => isToolUIPart(p) && p.state === 'output-available');
        },
    });

    // Deferred send: when a new chat is created, we set pendingText and activeChatId.
    // useChat re-initializes with the new id on re-render, then this effect fires sendMessage.
    useEffect(() => {
        if (activeChatId && pendingText && transport) {
            transport.setChatId(activeChatId);
            sendMessage({text: pendingText});
            setPendingText(null);
        }
    }, [activeChatId, pendingText, transport, sendMessage]);

    const sending = status === 'streaming' || status === 'submitted';

    // Load messages when switching chats (skip if we're about to send on a new chat)
    useEffect(() => {
        if (!activeChatId || !credentials) {
            setMessages([]);
            return;
        }
        if (sending || pendingText) return;
        getMessages(activeChatId, credentials).then((msgs) => setMessages(msgs));
    }, [activeChatId, credentials]); // eslint-disable-line react-hooks/exhaustive-deps -- sending/pendingText are guards, not triggers

    const handleSend = useCallback(
        async (overrideText?: string) => {
            const text = (overrideText ?? input).trim();
            if (!text || sending || !credentials || !transport) return;

            setInput('');

            try {
                if (!activeChatId) {
                    const chat = await createChat(text.slice(0, 50), credentials);
                    setActiveChatId(chat.id);
                    setPendingText(text);
                    return chat.id;
                } else {
                    transport.setChatId(activeChatId);
                    sendMessage({text});
                    return activeChatId;
                }
            } catch (e) {
                setInput(text);
                console.error('Failed to send message:', e);
            }
        },
        [input, sending, activeChatId, credentials, sendMessage, transport, setActiveChatId]
    );

    const handleNewChat = useCallback(() => {
        setActiveChatId(null);
        setMessages([]);
        setInput('');
    }, [setMessages, setActiveChatId]);

    return {
        messages,
        status,
        sending,
        input,
        setInput,
        transport,
        handleSend,
        handleNewChat,
        setMessages,
        stop,
        addToolResult,
    };
}
